import React from "react";
import { MDXProvider } from "@mdx-js/react";
import matter from "gray-matter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ImageLoader from "./ImageLoader";
import YouTubeEmbed from "./YouTubeEmbed";
import { Link as LinkIcon } from "lucide-react";

// Simple slug generator to create anchor links for headings
const slugify = (value: React.ReactNode): string | undefined => {
  if (!value) return undefined;
  // Convert ReactNode to plain string
  let text = "";
  if (Array.isArray(value)) {
    text = value
      .map((child) => (typeof child === "string" ? child : ""))
      .join("");
  } else if (typeof value === "string") {
    text = value;
  } else if (
    typeof value === "object" &&
    "props" in value &&
    "children" in value.props &&
    typeof value.props.children === "string"
  ) {
    text = value.props.children;
  }

  text = text.trim();

  if (!text) return undefined;

  return (
    text
      .toLowerCase()
      // Replace non-alphanumeric characters with spaces
      .replace(/[^a-z0-9\s-]/g, "")
      // Replace whitespace with single dash
      .replace(/\s+/g, "-")
      // Collapse multiple dashes
      .replace(/-+/g, "-")
      // Trim leading/trailing dashes
      .replace(/^-|-$/g, "")
  );
};

// Individual heading components (capitalized so Hooks are allowed)
const H1: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({
  children,
  ...props
}) => {
  const id = slugify(children);

  const updateUrl = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id) return;

    const newHashUrl = `${window.location.pathname}#${id}`;

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", newHashUrl);
    } else {
      window.location.hash = id;
    }

    // Smooth scroll to the heading
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <h1
      id={id}
      className="relative group flex items-center text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-foreground"
      {...props}
    >
      <button
        onClick={updateUrl}
        aria-label="Update URL"
        className="absolute -left-8 inset-y-0 w-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
      >
        <LinkIcon size={18} />
      </button>
      {children}
    </h1>
  );
};

const H2: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({
  children,
  ...props
}) => {
  const id = slugify(children);

  const updateUrl = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id) return;

    const newHashUrl = `${window.location.pathname}#${id}`;

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", newHashUrl);
    } else {
      window.location.hash = id;
    }

    // Smooth scroll to the heading
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <h2
      id={id}
      className="relative group flex items-center text-xl md:text-2xl font-bold mt-8 md:mt-10 mb-3 md:mb-4 text-foreground"
      {...props}
    >
      <button
        onClick={updateUrl}
        aria-label="Update URL"
        className="absolute -left-8 inset-y-0 w-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
      >
        <LinkIcon size={16} />
      </button>
      {children}
    </h2>
  );
};

const H3: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({
  children,
  ...props
}) => {
  const id = slugify(children);

  const updateUrl = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id) return;

    const newHashUrl = `${window.location.pathname}#${id}`;

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", newHashUrl);
    } else {
      window.location.hash = id;
    }

    // Smooth scroll to the heading
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <h3
      id={id}
      className="relative group flex items-center text-lg md:text-xl font-bold mt-6 md:mt-8 mb-2 md:mb-3 text-foreground"
      {...props}
    >
      <button
        onClick={updateUrl}
        aria-label="Update URL"
        className="absolute -left-8 inset-y-0 w-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
      >
        <LinkIcon size={14} />
      </button>
      {children}
    </h3>
  );
};

const components = {
  h1: H1,
  h2: H2,
  h3: H3,
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => {
    if (props.children && typeof props.children === "string") {
      const content = props.children;
      if (
        /^id:|^title:|^excerpt:|^date:|^category:|^readTime:|^imageUrl:/.test(
          content
        ) ||
        content.includes("---")
      ) {
        return null;
      }
    }
    return (
      <p
        className="mb-4 md:mb-6 leading-relaxed text-base font-['Inter',sans-serif] tracking-wide text-foreground dark:text-[#F1F1F1]"
        {...props}
      />
    );
  },
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="list-disc my-4 md:my-6 pl-5" {...props} />
  ),
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className="list-decimal my-4 md:my-6 pl-8 md:pl-10" {...props} />
  ),
  li: (props: React.HTMLAttributes<HTMLLIElement>) => (
    <li
      className="mb-2 font-['Inter',sans-serif] tracking-wide leading-relaxed text-foreground dark:text-[#F1F1F1] pl-4 ml-2"
      {...props}
    />
  ),
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    const href = props.href || "";
    const isExternal =
      href.startsWith("http") && !href.includes(window.location.hostname);
    return (
      <a
        className="blog-post-link"
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        {...props}
      />
    );
  },
  blockquote: (props: React.BlockquoteHTMLAttributes<HTMLQuoteElement>) => (
    <blockquote
      className="border-l-4 border-primary/30 pl-4 italic my-4 md:my-6"
      {...props}
    />
  ),
  table: (props: React.TableHTMLAttributes<HTMLTableElement>) => (
    <div className="my-6 w-full overflow-x-auto">
      <Table
        className="bg-card text-foreground border border-border dark:border-border"
        {...props}
      />
    </div>
  ),
  thead: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <TableHeader className="bg-muted/50 dark:bg-muted/30" {...props} />
  ),
  tbody: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <TableBody {...props} />
  ),
  tr: (props: React.HTMLAttributes<HTMLTableRowElement>) => (
    <TableRow className="hover:bg-muted/30 dark:hover:bg-muted/10" {...props} />
  ),
  th: (props: React.ThHTMLAttributes<HTMLTableCellElement>) => (
    <TableHead
      className="border-r border-border dark:border-border last:border-r-0"
      {...props}
    />
  ),
  td: (props: React.TdHTMLAttributes<HTMLTableCellElement>) => (
    <TableCell
      className="border-r border-border dark:border-border last:border-r-0"
      {...props}
    />
  ),
  pre: (props: React.HTMLAttributes<HTMLPreElement>) => {
    if (props.children && typeof props.children === "string") {
      const content = props.children;
      if (
        content.includes("---") &&
        (content.includes("id:") || content.includes("title:"))
      ) {
        return null;
      }
    }
    return (
      <pre
        className="bg-secondary/50 p-3 md:p-4 rounded-md overflow-x-auto font-mono text-xs md:text-sm my-4 md:my-6"
        {...props}
      />
    );
  },
  code: (props: React.HTMLAttributes<HTMLElement>) => {
    const className = props.className || "";
    const isInline = !className.includes("language-");
    return isInline ? (
      <code
        className="bg-secondary/70 px-1.5 py-0.5 rounded text-sm font-mono"
        {...props}
      />
    ) : (
      <code {...props} />
    );
  },
  img: ({
    src,
    alt,
    ...props
  }: React.ImgHTMLAttributes<HTMLImageElement> & { _firstImage?: boolean }) => {
    const aspectRatio = 16 / 9;
    const defaultWidth = 800;
    const defaultHeight = Math.round(defaultWidth / aspectRatio);

    return (
      <ImageLoader
        src={src || ""}
        alt={alt || "Blog image"}
        className="w-full h-auto rounded-md my-6"
        priority={props._firstImage === true}
        width={defaultWidth}
        height={defaultHeight}
      />
    );
  },
  YouTubeEmbed: (props: { videoId: string; title?: string }) => (
    <div className="w-full my-6">
      <div className="aspect-w-16 aspect-h-9 relative">
        <iframe
          src={`https://www.youtube.com/embed/${props.videoId}`}
          title={props.title || "YouTube video player"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full rounded-lg shadow-lg"
          width="1280"
          height="720"
        />
      </div>
    </div>
  ),
};

interface MDXWrapperProps {
  children: React.ReactNode;
}

const MDXWrapper: React.FC<MDXWrapperProps> = ({ children }) => {
  return <MDXProvider components={components}>{children}</MDXProvider>;
};

export default MDXWrapper;
